﻿/*!
  * Furillo navigation.js v5.3.2
  * Copyright 2023 Furillo Agency AB (https://www.furillo.se/)  
  */
import Dropdown from 'Vendor/bootstrap/js/dist/dropdown.js';
import ajaxPartials from 'Furillo/modules/ajax-partials.js';

export function extendedMainNavigation(element) {

    if (!element)
        return;

    // Use named handlers in order to be able to remove event listeners
    const showDropdown = function (evt) {
        Dropdown.getOrCreateInstance(evt.target.querySelector('.dropdown-toggle')).show();
    };

    const hideDropdown = function (evt) {
        Dropdown.getOrCreateInstance(evt.target.querySelector('.dropdown-toggle')).hide();
    };

    const showActiveDropdowns = function (evt) {
        evt.target.querySelectorAll('.dropdown.active-child > .dropdown-toggle, .dropdown.active > .dropdown-toggle').forEach(dropdown => { Dropdown.getOrCreateInstance(dropdown).show(); });
    };

    const dropdownShowing = function (evt) {
        evt.target.nextElementSibling.classList.add('showing');
    };

    const dropdownHiding = function (evt) {
        const dropdown = evt.target.nextElementSibling;

        dropdown.classList.remove('showing');
        dropdown.classList.add('hiding');
    };

    const dropdownHidden = function (evt) {
        const dropdown = evt.target.nextElementSibling;

        dropdown.setAttribute('data-bs-popper', 'none'); // add to prevent Popper to hijack positioning
        dropdown.onanimationend = () => {
            dropdown.classList.remove('hiding');
        };
    };

    const requestDropdownContent = function (evt) {
        const ul = evt.target.nextElementSibling;
        if (ul.hasChildNodes())
            return;

        ajaxPartials.makeRequest(ul);
    };

    const init = function () {
        const navbarToggler = element.querySelector('button.navbar-toggler');
        const navbar = element.querySelector(navbarToggler.getAttribute('data-bs-target'));
               
        // Remove bindings from larger screens
        element.querySelectorAll('li.dropdown').forEach(element => {
            element.removeEventListener('mouseenter', showDropdown);
            element.removeEventListener('mouseleave', hideDropdown);
        });

        // Remove binding from smaller screens
        navbar.removeEventListener('shown.bs.collapse', showActiveDropdowns);
        navbar.removeEventListener('shown.bs.offcanvas', showActiveDropdowns);
        navbar.removeAttribute('aria-hidden');

        // Remove bindings for ajax dropdowns
        navbar.querySelectorAll('li.dropdown > ul.dropdown-menu[data-ajax-url]').forEach(dropdown => { dropdown.parentElement.removeEventListener('show.bs.dropdown', requestDropdownContent); });

        // We can't use visibility of "navbar-toggler" since this can be used in a "hybrid menu"
        // where mobile nav is always visible and top menu are used as main menu on desktop sizes.
        // Instead get actual break point from element set by CSS.
        let breakpoint = window.getComputedStyle(element, '::after').getPropertyValue('content');
        if (!breakpoint || breakpoint === 'none')
            return;

        breakpoint = parseFloat(breakpoint.replace(/"/g, '').replace('px', ''));
        if (isNaN(breakpoint))
            return;

        // Add extra classes to enable flexibility for CSS animations and
        // workaround that BS Dropdown doesn't wait for CSS animations to finish.
        navbar.addEventListener('show.bs.dropdown', dropdownShowing);
        navbar.addEventListener('hide.bs.dropdown', dropdownHiding);
        navbar.addEventListener('hidden.bs.dropdown', dropdownHidden);

        const initDesktopMenu = function () {
            // Change the auto close behaviour
            element.querySelectorAll('.dropdown-toggle[data-bs-auto-close="false"]').forEach(dropdown => { dropdown.setAttribute('data-bs-auto-close', 'outside'); });

            // Get all 2'nd level and deeper toggles and add "dropend" to their parents in order to open flyout to the right.
            element.querySelectorAll('ul.navbar-nav > .dropdown').forEach(element => {
                element.querySelectorAll('ul.dropdown-menu .dropdown > .dropdown-toggle').forEach(dropdown => { dropdown.parentElement.classList.add('dropend'); });
            });

            // Add listeners for "hover" for all dropdowns that are not disabled/hidden
            element.querySelectorAll('li.dropdown:not([data-furillo-nav-dropdown-hidden="true"])').forEach(element => {
                element.addEventListener('mouseenter', showDropdown);
                element.addEventListener('mouseleave', hideDropdown);
            });
        };

        const initMobileMenu = function () {

            // Remove the dropend class for all menus            
            navbar.querySelectorAll('ul.dropdown-menu .dropdown.dropend').forEach(element => { element.classList.remove('dropend'); });

            navbar.addEventListener('shown.bs.collapse', showActiveDropdowns);
            navbar.addEventListener('shown.bs.offcanvas', showActiveDropdowns);
            navbar.setAttribute('aria-hidden', 'true');

            // Remove any hovers
            navbar.querySelectorAll('li.dropdown').forEach(element => {
                element.removeEventListener('mouseenter', showDropdown);
                element.removeEventListener('mouseleave', hideDropdown);
            });

            // Change the auto close behaviour
            navbar.querySelectorAll('.dropdown-toggle[data-bs-auto-close="outside"]').forEach(dropdown => { dropdown.setAttribute('data-bs-auto-close', 'false'); });

            // Enable ajax for smaller screens
            navbar.querySelectorAll('li.dropdown > ul.dropdown-menu[data-ajax-url]').forEach(dropdown => { dropdown.parentElement.addEventListener('show.bs.dropdown', requestDropdownContent); });
        };

        // If larger than breakpoint we are on "desktop".        
        if (window.innerWidth > breakpoint) {

            initDesktopMenu(element);

            // When hybrid the navbars in the offcanvas should still act as
            // mobile when on desktop.
            if (element.classList.contains('navbar-hybrid'))
                initMobileMenu();

            return;
        }

        initMobileMenu();
    };

    // Re-init on resize
    window.addEventListener('resize', init);

    init();
}

export function extendedSubNavigation(element) {

    if (!element)
        return;

    const init = function () {

        // Ensure that we haven't already loaded the submenu, this happens
        // when changing window size but still on desktop size
        if (element.querySelector('ul'))
            return;

        // We can't use visibility of "navbar-toggler" since this can be used in a "hybrid menu"
        // where mobile nav is always visible and top menu are used as main menu on desktop sizes.
        // Instead get actual break point from element set by CSS.
        let breakpoint = window.getComputedStyle(element, '::after').getPropertyValue('content');
        if (!breakpoint || breakpoint === 'none')
            return;

        breakpoint = parseFloat(breakpoint.replace(/"/g, '').replace('px', ''));
        if (isNaN(breakpoint))
            return;

        // If smaller than breakpoint we are on "mobile", 
        // no need to render a submenu.
        if (window.innerWidth <= breakpoint)
            return;

        // When clicking on child collapses
        element.addEventListener('show.bs.collapse', evt => {
            const ul = evt.target;
            if (ul.hasChildNodes())
                return;

            ajaxPartials.makeRequest(ul);
        });

        // Get the first level
        ajaxPartials.makeRequest(element);
    };

    // Re-init on resize
    window.addEventListener('resize', init);

    init();
}